import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';

const CLASS_COLORS = {
  Fighter: '#e06050',
  Mage:    '#7eb8da',
  Cleric:  '#e8c170',
  Thief:   '#80c080',
  Psion:   '#c8a0e8',
  Druid:   '#6ec46e',
};

const CharacterSelectPage = () => {
  const { user, characters, isAuthenticated, hasCharacter, selectCharacter, deleteCharacter, fetchCharacters, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    // Refresh character list when page loads
    if (isAuthenticated) {
      fetchCharacters();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // If not logged in, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If already has a character selected, redirect to game
  if (hasCharacter) {
    return <Navigate to="/game" replace />;
  }

  const handleSelect = async (characterId) => {
    setLoading(true);
    const result = await selectCharacter(characterId);
    if (result.success) {
      toast.success('Entering the realm...');
      navigate('/game');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleDelete = async (characterId) => {
    if (confirmDelete !== characterId) {
      setConfirmDelete(characterId);
      return;
    }
    setDeletingId(characterId);
    const result = await deleteCharacter(characterId);
    if (result.success) {
      toast.success('Character deleted');
      setConfirmDelete(null);
    } else {
      toast.error(result.error);
    }
    setDeletingId(null);
  };

  return (
    <div className="auth-container">
      <div className="auth-form fade-in" style={{ maxWidth: 600 }}>
        <h2 style={{ textAlign: 'center', color: '#d4af37', marginBottom: 4 }}>
          The Realms of Loria
        </h2>
        <p style={{ textAlign: 'center', color: '#8a8a9a', fontSize: 13, marginBottom: 20 }}>
          Welcome, <strong style={{ color: '#ccc' }}>{user?.username}</strong> — choose a character to play
        </p>

        {/* Phase 6 — email verification banner. Hidden when no email transport
             is configured (SENDGRID_API_KEY unset) so we don't ask players to
             check an inbox we can't deliver to. */}
        {user && user.verified === false && user.emailEnabled !== false && (
          <div style={{
            padding: '10px 14px',
            marginBottom: 16,
            background: '#3a2f1a',
            border: '1px solid #a07a2a',
            borderRadius: 6,
            color: '#e8d48a',
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}>
            <span>
              Your email isn't verified yet. Check your inbox for the verification link.
            </span>
            <button
              type="button"
              className="btn btn-sm"
              style={{ flexShrink: 0 }}
              onClick={async () => {
                try {
                  await axios.post('/api/auth/resend-verification');
                  toast.success('Verification email re-sent. Check your inbox.');
                } catch (err) {
                  toast.error(err?.response?.data?.error || 'Could not resend verification email');
                }
              }}
            >
              Resend
            </button>
          </div>
        )}

        {/* Character cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {characters.length === 0 ? (
            <div style={{
              padding: 24, textAlign: 'center', color: '#8a8a9a', fontSize: 14,
              background: '#1a1a2e', borderRadius: 8, border: '1px dashed #333'
            }}>
              No characters yet — create one to begin your adventure!
            </div>
          ) : (
            characters.map(char => (
              <div
                key={char.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', borderRadius: 8,
                  background: '#1a1a2e', border: '1px solid #2a2a3a',
                  cursor: loading ? 'default' : 'pointer',
                  transition: 'border-color 0.2s, background 0.2s',
                }}
                className="char-select-card"
                onClick={() => !loading && handleSelect(char.id)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#d4af37'; e.currentTarget.style.background = '#1e1e36'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a3a'; e.currentTarget.style.background = '#1a1a2e'; }}
              >
                {/* Class icon / color dot */}
                <div className="char-select-dot" style={{
                  background: CLASS_COLORS[char.class] || '#555',
                }}>
                  {char.class?.[0] || '?'}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#e0e0e0' }}>
                    {char.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#8a8a9a', marginTop: 2 }}>
                    Level {char.level} {char.race} {char.advanced_class || char.class}
                    {char.alignment ? ` · ${char.alignment}` : ''}
                  </div>
                </div>

                {/* Gold / XP */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: '#d4af37' }}>{char.gold || 0} gp</div>
                  <div style={{ fontSize: 11, color: '#666' }}>{char.experience || 0} xp</div>
                </div>

                {/* Delete button */}
                {characters.length > 1 && (
                  <button
                    onClick={e => { e.stopPropagation(); handleDelete(char.id); }}
                    disabled={deletingId === char.id}
                    title={confirmDelete === char.id ? 'Click again to confirm' : 'Delete character'}
                    style={{
                      background: confirmDelete === char.id ? '#cc3333' : 'transparent',
                      border: confirmDelete === char.id ? '1px solid #cc3333' : '1px solid #444',
                      color: confirmDelete === char.id ? '#fff' : '#666',
                      borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                      flexShrink: 0, transition: 'all 0.2s'
                    }}
                  >
                    {deletingId === char.id ? '...' : confirmDelete === char.id ? 'Confirm?' : '✕'}
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Create new character */}
        {characters.length < 5 && (
          <button
            className="btn btn-primary"
            style={{ width: '100%', marginBottom: 8 }}
            onClick={() => navigate('/create-character')}
            disabled={loading}
          >
            + Create New Character
          </button>
        )}

        {characters.length >= 5 && (
          <p style={{ textAlign: 'center', color: '#666', fontSize: 12, marginBottom: 8 }}>
            Maximum of 5 characters reached
          </p>
        )}

        {/* Logout */}
        <button
          className="btn"
          style={{ width: '100%', background: '#333', color: '#999', fontSize: 13 }}
          onClick={logout}
        >
          Log Out
        </button>
      </div>
    </div>
  );
};

export default CharacterSelectPage;
